import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Keyboard,
    ListRenderItemInfo,
    Platform,
    ScrollView,
    View,
} from 'react-native';

import { useTranslation } from 'react-i18next';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Edge, SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';

import { BottomSheetModalMethods } from '@gorhom/bottom-sheet/lib/typescript/types';
import { RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { CloseIcon } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    DELETE_PROFILE_DESCRIPTION,
    DELETE_PROFILE_TITLE,
    EDIT_PROFILE_DELETE_BUTTON,
    EDIT_PROFILE_DESCRIPTION,
    EDIT_PROFILE_SAVE_BUTTON,
    EDIT_PROFILE_TITLE,
    PROFILE_AGE_RATING_ITEM,
    PROFILE_BACK_BUTTON,
    PROFILE_CLOSE_BUTTON,
    PROFILE_DELETE_BUTTON,
    PROFILE_DELETE_NOT_ALLOWED_BODY,
    PROFILE_DELETE_NOT_ALLOWED_TITLE,
    PROFILE_DELETE_PROFILE_ERROR_CODE,
    PROFILE_DELETE_PROFILE_ERROR_DESCRIPTION,
    PROFILE_DELETE_PROFILE_ERROR_TITLE,
    PROFILE_EDIT_PROFILE_ERROR_CODE,
    PROFILE_EDIT_PROFILE_ERROR_DESCRIPTION,
    PROFILE_EDIT_PROFILE_ERROR_TITLE,
    PROFILE_KEEP_BUTTON,
    PROFILE_KIDS_NOT_ALLOWED_DESCRIPTION,
    PROFILE_KIDS_NOT_ALLOWED_TITLE,
    PROFILE_OK_BUTTON,
    PROFILE_TRY_AGAIN_BUTTON,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import {
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import {
    deleteProfilePlayerAudio,
    deleteProfilePlayerSubtitles,
} from 'store/player/actions';
import {
    changeUserProfileAvatar,
    changeUserProfileRating,
    deleteProfile,
    editProfile,
    editProfileRequestReset,
    updateRatingsThresholds,
} from 'store/user/actions';
import { ParentalControlRating } from 'store/user/reducer.types';
import {
    selectActiveUserProfile,
    selectDeleteProfileErrorCode,
    selectDeleteProfileErrorStatus,
    selectEditProfileErrorCode,
    selectEditProfileErrorStatus,
    selectParentalControlRating,
    selectProfileAvatar,
    selectProfileParentalRating,
    selectProfilesList,
} from 'store/user/selectors';

import { COLOR } from 'utils/colors';
import useBackHandler from 'utils/hooks/useBackHandler.ts';
import useNoInternet from 'utils/hooks/useNoInternet';
import useSafeAreaEdges from 'utils/hooks/useSafeAreaEdges';
import { isPhone, isTabletOrWeb, isWeb } from 'utils/responsiveness';

import BottomSheetAccessibleList from 'components/BottomSheetAccessibleList';
import BottomSheetList from 'components/BottomSheetList';
import rowStyles from 'components/BottomSheetRowItem/styles';
import SvgButton from 'components/CustomButtons/SvgButton';
import DialogView from 'components/Dialog';
import DialogBody from 'components/Dialog/components/DialogBody';
import ErrorDialog from 'components/ErrorDialog';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import { setParentalRating } from '../AddProfile/utils';
import {
    adultProfileRating,
    kidProfileRating,
} from '../ProfileAgeRating/constants';
import AgeRowItem from '../components/AgeRowItem';
import ProfileForm from '../components/ProfileForm';
import {
    deletedProfileErrorCodesArray,
    validateProfileName,
    validateProfileNameToExistingNames,
} from '../utils';
import styles from './styles';

type Props = {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        SCREEN.MANAGE_PROFILE
    >;
    route: RouteProp<RouteNavigationParams, SCREEN.MANAGE_PROFILE>;
};

const {
    PROFILE: {
        PROFILES_EDIT_PROFILE_TITLE,
        PROFILES_EDIT_PROFILE_DESCRIPTION,
        BUTTON_SAVE,
        PROFILE_ADD_CHANE_ERROR_TITLE,
        PROFILE_ADD_CHANE_ERROR_DESCRIPTION,
        DELETE_BUTTON,
        PROFILE_DELETE_PROFILE_TITLE,
        PROFILE_DELETE_PROFILE_DESCRIPTION,
        KEEP_BUTTON,
        PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_TITLE,
        PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_BODY,
        PROFILE_ERROR_ALERT_DELETE_NOT_ALLOWED_TITLE,
        PROFILE_ERROR_ALERT_DELETE_NOT_ALLOWED_BODY,
        PROFILE_ERROR_PROFILE_ALREADY_DELETED_TITLE,
        PROFILE_ERROR_PROFILE_ALREADY_DELETED_DESCRIPTION,
        PROFILE_AGE_RATING_BUTTON,
        PROFILE_KIDS_AGE_BUTTON,
    },
    TRY_AGAIN_BUTTON,
    BACK_BUTTON,
    OK_BUTTON,
    ACCESSIBILITY: {
        LOGIN: { ACCESSIBILITY_CLOSE_BUTTON },
    },
} = LANGUAGE_KEYS;

function ManageProfilePage({ navigation, route }: Props) {
    const { t } = useTranslation();
    const {
        primary,
        error_color: errorColor,
        background,
    } = useSelector(selectLayoutConfig);
    const editProfileErrorCode = useSelector(selectEditProfileErrorCode);
    const editProfileErrorStatus = useSelector(selectEditProfileErrorStatus);
    const deleteProfileErrorCode = useSelector(selectDeleteProfileErrorCode);
    const deleteProfileErrorStatus = useSelector(
        selectDeleteProfileErrorStatus,
    );
    const profiles = useSelector(selectProfilesList);
    const selectedProfileId = useSelector(selectActiveUserProfile);
    const profileParentalRating = useSelector(selectProfileParentalRating);
    const userParentalControlRating = useSelector(selectParentalControlRating);
    const profileAvatar = useSelector(selectProfileAvatar);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const dispatch = useDispatch();
    const {
        params: { name: selectedProfileName },
    } = route;

    const edges: Edge[] = useSafeAreaEdges();
    const { isInternetConnected } = useNoInternet();
    const selectedProfile = useMemo(
        () => profiles.find(({ name }) => name === selectedProfileName),
        [profiles, selectedProfileName],
    );
    const currentProfile = useMemo(
        () => profiles.find(({ _id }) => _id === selectedProfileId),
        [profiles, selectedProfileId],
    );

    const [selectedProfileNameErrorText, setSelectedProfileNameErrorText] =
        useState('');
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [showDeleteProfileDialog, setShowDeleteProfileDialog] =
        useState(false);
    const [wasDeleteProfileDialogShown, setWasDeleteProfileDialogShown] =
        useState(false);
    const [ratingData, setRatingData] = useState(
        selectedProfile?.childProfile ? kidProfileRating : adultProfileRating,
    );
    const [isBottomSheetOpened, setIsBottomSheetOpened] = useState(false);

    const selectedProfileNameRef = React.useRef(selectedProfile?.name || '');
    const bottomSheetRef = React.useRef<BottomSheetModalMethods>(undefined);

    const buttonStyle = useMemo(
        () => ({
            borderColor: primary,
        }),
        [],
    );

    const hasDeletedProfileError = useMemo(() => {
        return (
            deletedProfileErrorCodesArray.includes(deleteProfileErrorCode) ||
            deletedProfileErrorCodesArray.includes(editProfileErrorCode)
        );
    }, [deleteProfileErrorCode, editProfileErrorCode]);

    const errorContent = useMemo(() => {
        if (currentProfile?.childProfile) {
            return {
                title: PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_TITLE,
                description: PROFILE_ERROR_TITLE_KIDS_NOT_ALLOWED_BODY,
                actionButtonText: undefined,
                actionButtonTestID: undefined,
                cancelButtonText: OK_BUTTON,
                cancelButtonTestID: PROFILE_OK_BUTTON,
                titleTestID: PROFILE_KIDS_NOT_ALLOWED_TITLE,
                descriptionTestID: PROFILE_KIDS_NOT_ALLOWED_DESCRIPTION,
                errorCodeTestID: wasDeleteProfileDialogShown
                    ? PROFILE_DELETE_PROFILE_ERROR_CODE
                    : PROFILE_EDIT_PROFILE_ERROR_CODE,
            };
        }

        if (deleteProfileErrorCode === 8125) {
            return {
                title: PROFILE_ERROR_ALERT_DELETE_NOT_ALLOWED_TITLE,
                description: PROFILE_ERROR_ALERT_DELETE_NOT_ALLOWED_BODY,
                actionButtonText: OK_BUTTON,
                actionButtonTestID: PROFILE_OK_BUTTON,
                cancelButtonText: undefined,
                cancelButtonTestID: undefined,
                titleTestID: PROFILE_DELETE_NOT_ALLOWED_TITLE,
                descriptionTestID: PROFILE_DELETE_NOT_ALLOWED_BODY,
                errorCodeTestID: PROFILE_DELETE_PROFILE_ERROR_CODE,
            };
        }

        if (hasDeletedProfileError) {
            return {
                title: PROFILE_ERROR_PROFILE_ALREADY_DELETED_TITLE,
                description: PROFILE_ERROR_PROFILE_ALREADY_DELETED_DESCRIPTION,
                actionButtonText: OK_BUTTON,
                actionButtonTestID: PROFILE_OK_BUTTON,
                cancelButtonText: undefined,
                cancelButtonTestID: undefined,
                titleTestID: PROFILE_DELETE_NOT_ALLOWED_TITLE,
                descriptionTestID: PROFILE_DELETE_NOT_ALLOWED_BODY,
                errorCodeTestID: PROFILE_DELETE_PROFILE_ERROR_CODE,
            };
        }

        if (wasDeleteProfileDialogShown) {
            return {
                title: PROFILE_ADD_CHANE_ERROR_TITLE,
                description: PROFILE_ADD_CHANE_ERROR_DESCRIPTION,
                actionButtonText: TRY_AGAIN_BUTTON,
                actionButtonTestID: PROFILE_TRY_AGAIN_BUTTON,
                cancelButtonText: BACK_BUTTON,
                cancelButtonTestID: PROFILE_BACK_BUTTON,
                titleTestID: PROFILE_DELETE_PROFILE_ERROR_TITLE,
                descriptionTestID: PROFILE_DELETE_PROFILE_ERROR_DESCRIPTION,
                errorCodeTestID: PROFILE_DELETE_PROFILE_ERROR_CODE,
            };
        }

        return {
            title: PROFILE_ADD_CHANE_ERROR_TITLE,
            description: PROFILE_ADD_CHANE_ERROR_DESCRIPTION,
            actionButtonText: TRY_AGAIN_BUTTON,
            actionButtonTestID: PROFILE_TRY_AGAIN_BUTTON,
            cancelButtonText: BACK_BUTTON,
            cancelButtonTestID: PROFILE_BACK_BUTTON,
            titleTestID: PROFILE_EDIT_PROFILE_ERROR_TITLE,
            descriptionTestID: PROFILE_EDIT_PROFILE_ERROR_DESCRIPTION,
            errorCodeTestID: PROFILE_EDIT_PROFILE_ERROR_CODE,
        };
    }, [
        currentProfile,
        wasDeleteProfileDialogShown,
        deleteProfileErrorCode,
        editProfileErrorCode,
    ]);

    const handleSetSelectedProfleNameToRef = useCallback((text: string) => {
        selectedProfileNameRef.current = text;
    }, []);

    const handleClose = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            navigation.reset({ routes: [{ name: SCREEN.PROFILES }] });
        }

        setTimeout(() => {
            dispatch(changeUserProfileRating(null));
            dispatch(changeUserProfileAvatar(null));
        }, 300);
    }, [navigation, dispatch]);

    const handleHideErrorDialog = useCallback(() => {
        setShowErrorDialog(false);
    }, []);

    const handleErrorDialogClose = useCallback(() => {
        handleHideErrorDialog();

        if (hasDeletedProfileError) {
            navigation.reset({ routes: [{ name: SCREEN.PROFILES }] });
            dispatch(editProfileRequestReset());
        }
    }, [dispatch, handleHideErrorDialog, hasDeletedProfileError, navigation]);

    const handleCloseErrorDialog = useCallback(() => {
        handleClose();
        handleHideErrorDialog();
    }, [handleClose, handleHideErrorDialog]);

    const handleSuccessEditProfile = useCallback(() => {
        if (selectedProfile && selectedProfile.default) {
            dispatch(
                updateRatingsThresholds(
                    setParentalRating(
                        profileParentalRating,
                        selectedProfile.childProfile,
                    ).rating,
                    {
                        autoRetry: true,
                        retryDuration: 10 * 60 * 1000,
                        retryCount: 10,
                    },
                ),
            );
        }

        handleClose();
    }, [profileParentalRating, selectedProfile]);

    const handleActionButtonPress = useCallback(() => {
        handleHideErrorDialog();
        setWasDeleteProfileDialogShown(false);

        const emptyNameError = validateProfileName(
            selectedProfileNameRef.current.trim(),
        );
        const existingNameError = validateProfileNameToExistingNames(
            selectedProfileNameRef.current,
            profiles.filter(({ _id }) => _id !== selectedProfile?._id),
        );
        setSelectedProfileNameErrorText(emptyNameError || existingNameError);

        if (
            emptyNameError === '' &&
            existingNameError === '' &&
            selectedProfile
        ) {
            dispatch(
                editProfile(
                    {
                        id: selectedProfile._id,
                        name: selectedProfileNameRef.current,
                        iconName: profileAvatar || selectedProfile.iconName,
                        ratingsThreshold: setParentalRating(
                            profileParentalRating,
                            selectedProfile.childProfile,
                        ).rating,
                    },
                    {
                        onSuccess: handleSuccessEditProfile,
                        onFailure: () => setShowErrorDialog(true),
                    },
                ),
            );
        }
    }, [
        profiles,
        selectedProfile,
        handleClose,
        profileParentalRating,
        handleHideErrorDialog,
        handleSuccessEditProfile,
        profileAvatar,
    ]);

    const handleCancelButtonPress = useCallback(() => {
        setShowDeleteProfileDialog(true);
    }, []);

    const handleEndEditing = useCallback(() => {
        selectedProfileNameErrorText &&
            setSelectedProfileNameErrorText(
                validateProfileName(selectedProfileNameRef.current),
            );
    }, [selectedProfileNameErrorText]);

    const handleDeleteAccountSuccessAction = useCallback(() => {
        if (selectedProfile) {
            dispatch(deleteProfilePlayerSubtitles(selectedProfile._id));
            dispatch(deleteProfilePlayerAudio(selectedProfile._id));
        }

        if (selectedProfile && selectedProfile._id === selectedProfileId) {
            navigation.reset({ routes: [{ name: SCREEN.PROFILES }] });
        } else {
            handleClose();
        }
    }, [selectedProfile, selectedProfileId, handleClose, dispatch]);

    const handleDeleteActionButtonPress = useCallback(() => {
        handleHideErrorDialog();
        setWasDeleteProfileDialogShown(false);

        if (selectedProfile) {
            dispatch(
                deleteProfile(
                    { id: selectedProfile._id },
                    {
                        onSuccess: handleDeleteAccountSuccessAction,
                        onFailure: () => {
                            setShowErrorDialog(true);
                            setShowDeleteProfileDialog(false);
                            setWasDeleteProfileDialogShown(true);
                        },
                    },
                ),
            );
        }
    }, [
        selectedProfile,
        handleDeleteAccountSuccessAction,
        handleHideErrorDialog,
    ]);

    const handleKeepButtonPress = useCallback(() => {
        setShowDeleteProfileDialog(false);
    }, []);

    const handleCloseBottomSheet = useCallback(() => {
        bottomSheetRef.current?.dismiss();
        setIsBottomSheetOpened(false);
    }, []);

    const handleChooseRating = useCallback(
        (item: {
            label: string;
            key: ParentalControlRating;
            isSelected: boolean;
        }) => {
            dispatch(
                changeUserProfileRating({
                    rating: item.key,
                    key: item.key,
                }),
            );

            setRatingData((prevData) =>
                prevData.map((prevItem) => ({
                    ...prevItem,
                    isSelected: prevItem.key === item.key,
                })),
            );

            handleCloseBottomSheet();
        },
        [handleCloseBottomSheet, selectedProfile],
    );

    const handleOpenAgeRating = useCallback(() => {
        if (isTabletOrWeb) {
            navigation.push(SCREEN.PROFILE_AGE_RATING, {
                isChildModeEnabled: !!selectedProfile?.childProfile,
                ageRating: setParentalRating(
                    profileParentalRating,
                    !!selectedProfile?.childProfile,
                ),
            });
        } else {
            bottomSheetRef.current?.present();
            setIsBottomSheetOpened(true);
        }
    }, [navigation, selectedProfile, profileParentalRating]);

    const handleAvatarPress = useCallback(() => {
        navigation.push(SCREEN.PROFILE_AVATARS, {
            isChildModeEnabled: !!selectedProfile?.childProfile,
        });
    }, [navigation, selectedProfile]);

    const handleHardwareBackPress = useCallback(() => {
        if (isBottomSheetOpened) {
            handleCloseBottomSheet();
            return true;
        }
        return false;
    }, [handleCloseBottomSheet, isBottomSheetOpened]);

    const renderDescriptionComponent = useCallback(
        () => (
            <View>
                <Text
                    category={ETextCategory.body1}
                    color={ETextColor.light_grey}
                    style={styles.description}
                    testID={EDIT_PROFILE_DESCRIPTION}
                    {...(isWeb && { tabIndex: 0 })}
                    center
                >
                    {t(PROFILES_EDIT_PROFILE_DESCRIPTION)}
                </Text>
                <ProfileForm
                    onPress={handleOpenAgeRating}
                    text={selectedProfileNameRef.current}
                    profileNameErrorText={selectedProfileNameErrorText}
                    errorColor={errorColor}
                    iconName={selectedProfile?.iconName}
                    isChildModeEnabled={!!selectedProfile?.childProfile}
                    onEndEditing={handleEndEditing}
                    onChangeText={handleSetSelectedProfleNameToRef}
                    onAvatarPress={handleAvatarPress}
                />
            </View>
        ),
        [
            selectedProfileNameErrorText,
            errorColor,
            selectedProfile,
            handleEndEditing,
            handleSetSelectedProfleNameToRef,
            handleOpenAgeRating,
            handleAvatarPress,
        ],
    );

    const renderBottomSheetRow = useCallback(
        ({
            item,
        }: ListRenderItemInfo<{
            label: string;
            key: ParentalControlRating;
            isSelected: boolean;
        }>) => {
            return (
                <AgeRowItem
                    isSelected={item.isSelected}
                    key={item.key}
                    label={t(item.label)}
                    onSelect={() => handleChooseRating(item)}
                    testID={`${PROFILE_AGE_RATING_ITEM}_${item.key}`}
                    backgroundColor={COLOR.primary}
                />
            );
        },
        [handleChooseRating, selectedProfile],
    );

    useBackHandler(handleHardwareBackPress);

    useEffect(() => {
        if (selectedProfile) {
            const ratingsThreshold = (selectedProfile.default &&
            !selectedProfile.ratingsThresholds &&
            userParentalControlRating
                ? userParentalControlRating
                : selectedProfile.ratingsThresholds[0]
                      .ratingsThreshold) as unknown as ParentalControlRating;

            dispatch(
                changeUserProfileRating({
                    rating: ratingsThreshold,
                    key: ratingsThreshold,
                }),
            );
        }
    }, [selectedProfile, userParentalControlRating, dispatch]);

    useEffect(() => {
        if (profileParentalRating) {
            setRatingData((prevData) =>
                prevData.map((prevItem) => ({
                    ...prevItem,
                    isSelected: prevItem.key === profileParentalRating.key,
                })),
            );
        }
    }, [profileParentalRating]);

    return (
        <SafeAreaView style={styles.container} edges={edges}>
            <ScrollView
                contentContainerStyle={styles.contentContainer}
                scrollEnabled={!isWeb && !Keyboard.isVisible()}
            >
                <KeyboardAwareScrollView
                    contentContainerStyle={styles.keyboardAwareContainer}
                    scrollEnabled={!isWeb && !Keyboard.isVisible()}
                >
                    <DialogBody
                        style={[
                            styles.dialog,
                            { backgroundColor: background, marginBottom: 0 },
                        ]}
                        title={PROFILES_EDIT_PROFILE_TITLE}
                        titleTestID={EDIT_PROFILE_TITLE}
                        DescriptionComponent={renderDescriptionComponent}
                        actionButtonTitle={BUTTON_SAVE}
                        actionButtonAccessibilityLabel={t(BUTTON_SAVE)}
                        actionButtonFillColor={primary}
                        actionButtonStyle={[styles.button, buttonStyle]}
                        actionButtonTextStyle={styles.buttonText}
                        actionButtonTestID={EDIT_PROFILE_SAVE_BUTTON}
                        cancelButtonTitle={
                            selectedProfile?.default ? undefined : DELETE_BUTTON
                        }
                        cancelButtonAccessibilityLabel={t(DELETE_BUTTON)}
                        cancelButtonFillColor={background}
                        closeButtonTestID={EDIT_PROFILE_DELETE_BUTTON}
                        handleActionButtonPress={handleActionButtonPress}
                        handleCancelPress={handleCancelButtonPress}
                        actionButtonBordered
                        cancelButtonBordered
                        revertButtons
                    />
                    <SvgButton
                        Svg={CloseIcon}
                        style={styles.closeButton}
                        svgStyle={styles.closeSvg}
                        onPress={handleClose}
                        testID={PROFILE_CLOSE_BUTTON}
                        accessibilityLabel={t(ACCESSIBILITY_CLOSE_BUTTON)}
                        accessibilityRole={Platform.select({
                            web: 'imagebutton',
                            default: 'button',
                        })}
                        accessible
                    />
                    {isPhone && screenReaderEnabled && (
                        <BottomSheetAccessibleList
                            visible={isBottomSheetOpened}
                            data={ratingData}
                            renderRowItem={renderBottomSheetRow}
                            keyExtractor={keyExtractor}
                            onClose={handleCloseBottomSheet}
                            headerTitle={t(
                                currentProfile?.childProfile
                                    ? PROFILE_KIDS_AGE_BUTTON
                                    : PROFILE_AGE_RATING_BUTTON,
                            )}
                        />
                    )}
                    {isPhone && !screenReaderEnabled && (
                        <BottomSheetList
                            ref={bottomSheetRef}
                            data={ratingData}
                            renderRowItem={renderBottomSheetRow}
                            keyExtractor={keyExtractor}
                            rowHeight={rowStyles.container.height}
                            onClose={handleCloseBottomSheet}
                        />
                    )}

                    <DialogView
                        visible={showDeleteProfileDialog}
                        style={[styles.dialog, { backgroundColor: background }]}
                        title={PROFILE_DELETE_PROFILE_TITLE}
                        titleTestID={DELETE_PROFILE_TITLE}
                        description={PROFILE_DELETE_PROFILE_DESCRIPTION}
                        descriptionTestID={DELETE_PROFILE_DESCRIPTION}
                        actionButtonTitle={DELETE_BUTTON}
                        actionButtonAccessibilityLabel={t(DELETE_BUTTON)}
                        actionButtonFillColor={background}
                        cancelButtonStyle={[styles.button, buttonStyle]}
                        cancelButtonTextStyle={styles.buttonText}
                        actionButtonTestID={PROFILE_DELETE_BUTTON}
                        cancelButtonTitle={KEEP_BUTTON}
                        cancelButtonAccessibilityLabel={t(KEEP_BUTTON)}
                        cancelButtonFillColor={primary}
                        closeButtonTestID={PROFILE_KEEP_BUTTON}
                        actionButtonOnPress={handleDeleteActionButtonPress}
                        cancelButtonOnPress={handleKeepButtonPress}
                        actionButtonBordered
                        cancelButtonBordered
                        revertButtons
                    />

                    <ErrorDialog
                        visible={isInternetConnected && showErrorDialog}
                        animationType="fade"
                        title={errorContent.title}
                        titleTestID={errorContent.titleTestID}
                        description={errorContent.description}
                        descriptionTestID={errorContent.descriptionTestID}
                        errorCode={`${
                            wasDeleteProfileDialogShown
                                ? deleteProfileErrorCode || ''
                                : editProfileErrorCode || ''
                        }`}
                        errorCodeTestID={errorContent.errorCodeTestID}
                        actionButtonTitle={errorContent.actionButtonText}
                        actionButtonAccessibilityLabel={t(
                            errorContent.actionButtonText as string,
                        )}
                        actionButtonTestID={errorContent.actionButtonTestID}
                        actionButtonOnPress={handleErrorDialogClose}
                        cancelButtonTitle={errorContent.cancelButtonText}
                        cancelButtonAccessibilityLabel={t(
                            errorContent.cancelButtonText as string,
                        )}
                        closeButtonTestID={errorContent.cancelButtonTestID}
                        cancelButtonFillColor={primary}
                        cancelButtonStyle={[styles.button, buttonStyle]}
                        actionButtonStyle={[
                            styles.button,
                            !errorContent.cancelButtonText && buttonStyle,
                        ]}
                        actionButtonFillColor={
                            !errorContent.cancelButtonText ? primary : undefined
                        }
                        actionButtonTextStyle={
                            !errorContent.cancelButtonText
                                ? styles.buttonText
                                : undefined
                        }
                        cancelButtonTextStyle={styles.buttonText}
                        cancelButtonOnPress={handleCloseErrorDialog}
                        errorStatus={
                            deleteProfileErrorStatus || editProfileErrorStatus
                        }
                        actionButtonBordered
                        revertButtons
                    />
                </KeyboardAwareScrollView>
            </ScrollView>
        </SafeAreaView>
    );
}

function keyExtractor({ key }: { key: ParentalControlRating | string }) {
    return key;
}

export default ManageProfilePage;
